import React from "react";
import PolicyLayout from "./PolicyLayout";
import "../css/policy.scss";

const sections = [
  {
    id: "agreement",
    title: "About these terms",
    body: [
      "These terms govern access to the nartvmora website and purchases made through it. The policies linked from these terms form part of the agreement where they apply to a visit or order.",
      "By using the website, a visitor agrees to these terms. If a person does not agree, they should not use the service or place an order. Questions can be sent to service@nartvmora.site.",
    ],
  },
  {
    id: "eligibility",
    title: "Eligibility and authority",
    body: [
      "A customer must be able to enter a binding contract in the place where they live. A person ordering for a business or another person confirms that they have authority to bind that party and use the supplied contact, delivery and payment information.",
    ],
  },
  {
    id: "orders",
    title: "Orders and contract formation",
    body: [
      "Submitting an order is an offer to buy the products shown at checkout. An acknowledgement confirms receipt, not acceptance. The order is accepted only after payment, stock and order checks are completed and an order confirmation is issued.",
      "We may reject, limit or cancel an order for unavailable stock, an obvious error, failed payment, suspected fraud, unauthorised resale, a delivery restriction or another lawful reason. Any captured payment for a cancelled item will be returned to the original payment method.",
    ],
    links: [{ to: "/orders", label: "Orders & cancellations" }],
  },
  {
    id: "products-pricing",
    title: "Products, pricing and availability",
    body: [
      "We take reasonable care with descriptions, measurements, images, prices and inventory, but display settings and natural denim finishes can create variation. Prices are shown in USD and may change before an order is placed.",
      "Adding an item to the bag does not reserve it. If an obvious price or description error is found before acceptance, we may ask the customer to confirm the corrected order or cancel it and issue any required refund.",
    ],
    links: [{ to: "/pricing-availability", label: "Pricing & availability" }],
  },
  {
    id: "payment",
    title: "Payment, taxes and charges",
    body: [
      "Checkout redirects to an independent hosted payment provider. The payment methods offered are those displayed on that page. The customer authorises the transaction total shown before payment and confirms that they are permitted to use the selected method.",
      "Delivery charges and taxes collected by nartvmora are included in or identified with the checkout total. Import duties, destination taxes or carrier charges may remain the recipient's responsibility where checkout does not expressly say they have been collected.",
    ],
    links: [{ to: "/payments", label: "Payments & billing" }],
  },
  {
    id: "delivery",
    title: "Delivery, title and risk",
    body: [
      "Delivery estimates are not guaranteed. The customer is responsible for providing a complete deliverable address and for reviewing any carrier tracking or collection notice.",
      "Ownership transfers only after full payment. Responsibility for loss or damage transfers when the order is delivered to the customer or a person they identify, except where mandatory law provides otherwise or the customer independently appoints a carrier not offered by us.",
    ],
    links: [{ to: "/shipping", label: "Shipping & delivery" }],
  },
  {
    id: "returns-guarantees",
    title: "Returns and consumer rights",
    body: [
      "Change-of-mind returns are subject to the published Returns & Refunds and Sale & Promotions policies. Separate mandatory rights may apply to faulty, unsafe, damaged, incorrect or misdescribed products.",
      "Nothing in these terms excludes, restricts or modifies a guarantee, remedy, liability or other customer right that cannot lawfully be excluded or restricted.",
    ],
    links: [
      { to: "/returns", label: "Returns & refunds" },
      { to: "/consumer-guarantees", label: "Consumer guarantees" },
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: [
      "Visitors must not interfere with the website, bypass security or purchase limits, use another person's information without authority, introduce harmful code, commit fraud, scrape the service in a harmful or commercially exploitative way, or submit unlawful, abusive or infringing material.",
    ],
    links: [{ to: "/acceptable-use", label: "Acceptable use policy" }],
  },
  {
    id: "intellectual-property",
    title: "Intellectual property",
    body: [
      "The nartvmora name and original website text, product names, imagery, graphics, layout and software are owned by or licensed to the operator and may be protected by intellectual-property laws. Access to the website grants a limited, revocable right to use it for personal shopping; it does not transfer ownership or grant a right to republish or commercially exploit the content.",
    ],
    links: [{ to: "/intellectual-property", label: "IP & takedown policy" }],
  },
  {
    id: "third-parties",
    title: "Third-party services and links",
    body: [
      "Payment, delivery, hosting and linked services may be operated by independent providers under their own terms and privacy notices. We are responsible for our selection and use of providers to the extent required by law, but do not control every feature or policy of an independent service.",
    ],
  },
  {
    id: "availability",
    title: "Website availability and changes",
    body: [
      "We may maintain, update, suspend or withdraw website features. We do not promise continuous or error-free access, but this does not affect an accepted order or a right that has already arisen. Customers should retain copies of order confirmations and policies relevant to their purchase.",
    ],
  },
  {
    id: "liability",
    title: "Responsibility and liability",
    body: [
      "Each party remains responsible for loss that is a reasonably foreseeable result of its breach or negligence, subject to applicable law. We are not responsible for loss caused solely by information supplied incorrectly by the customer, an independent event outside reasonable control, or use of the website contrary to these terms.",
      "The website is provided for personal shopping and general product information. We do not exclude liability for fraud, wilful misconduct, death or personal injury caused by negligence, or any other liability that cannot lawfully be excluded or limited.",
    ],
  },
  {
    id: "events-control",
    title: "Events outside reasonable control",
    body: [
      "A delay caused by severe weather, transport disruption, government action, labour disruption, network failure or another event outside reasonable control will be addressed as soon as reasonably possible. If the delay becomes material, any cancellation or refund right required by law will be provided.",
    ],
  },
  {
    id: "disputes-law",
    title: "Concerns, governing law and disputes",
    body: [
      "Contact customer care first so a concern can be reviewed. The law and courts that apply depend on the operator, the customer's location and mandatory consumer rules. These terms do not deprive a customer of the protection of mandatory law or the right to use an available regulator, court or dispute-resolution body.",
      "If one provision is unenforceable, it will be limited or removed only to the extent required, and the remaining terms continue. A delay in enforcing a term does not waive it.",
    ],
    links: [{ to: "/contact", label: "Contact customer care" }],
  },
  {
    id: "changes",
    title: "Changes to these terms",
    body: [
      "We may update these terms prospectively when services or legal requirements change. The terms in effect when an order is placed continue to govern that order unless a change must apply by law or the customer agrees to it.",
    ],
  },
];

function Terms() {
  return (
    <PolicyLayout
      eyebrow="Legal"
      title="Terms of Use"
      intro="Terms for browsing nartvmora, placing an order and using our shopping services."
      updated="Effective 16 October 2026"
      sections={sections}
      action={{ to: "/contact", label: "Contact us" }}
    />
  );
}

export default Terms;
